import { formatCurrency, transformDateToLocalDateString, transformRoute } from "helpers";
import { FleetVehicleExpenseRow } from "services/api/fleet/vehicles/expenses/types";
import { Badge } from "ui/components/Badge";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import Tooltip from "ui/components/Tooltip/Tooltip";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import InvoiceIncomingCell from "ui/components/Table/TableSearch/components/Cells/InvoiceIncomingCell";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";

// Stupci za /finance/expenses/search. Sastavljeni su iz postojećeg per-vehicle
// trošak prikaza, plus prependanog "Vozilo" stupca koji koristi puni
// FleetVehicle objekt koji BE već vraća uz svaki red.
export const FINANCE_EXPENSE_SEARCH_COLUMNS = (
  t: (key: string) => string,
  onDeleteClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  onVehicleClick: (event: React.MouseEvent, vehicleId: string) => void
): Column[] => [
  {
    title: "Vozilo",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleExpenseRow) => {
      if (!row.vehicle) return null;
      return (
        <VehicleCell
          vehicle={row.vehicle}
          onClick={(e) => onVehicleClick(e, row.vehicle!.id)}
        />
      );
    },
  },
  {
    title: "admin.fleet.vehicle.expense.search.type",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleExpenseRow) => (
      <Badge
        text={row.expenseTypeName ?? "-"}
        variant="info"
        height="small"
        showDot
      />
    ),
  },
  {
    title: "admin.fleet.vehicle.expense.search.name",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleExpenseRow) => (
      <div>
        <div className="font-medium text-slate-900">{row.name}</div>
      </div>
    ),
  },
  {
    title: "admin.fleet.vehicle.expense.search.description",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleExpenseRow) => (
      <div>
        <div className="text-sm text-slate-600 max-w-[800px] overflow-hidden text-ellipsis line-clamp-1">
          {row.description}
        </div>
      </div>
    ),
  },
  {
    title: "Ulazni račun",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleExpenseRow) => {
      if (!row.invoiceIncoming)
        return <div className="text-sm text-slate-400">-</div>;
      return <InvoiceIncomingCell invoice={row.invoiceIncoming} />;
    },
  },
  {
    title: "admin.fleet.vehicle.expense.search.expensedate",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleExpenseRow) => (
      <div className="text-sm">
        {transformDateToLocalDateString(row.expenseDate)}
      </div>
    ),
  },
  {
    title: "admin.fleet.vehicle.expense.search.amount",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleExpenseRow) => (
      <Tooltip
        decorate={
          <span className="text-sm font-medium">
            {formatCurrency.format(row.amount ?? 0)}
          </span>
        }
        position="bottom-right"
        size="medium"
      >
        <div className="min-w-[220px]">
          <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">
            Pregled iznosa
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-300">Iznos bez PDV-a</span>
            <span className="font-medium">
              {formatCurrency.format(row.amount ?? 0)}
            </span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-300">PDV</span>
            <span className="font-medium">
              {formatCurrency.format(row.taxAmount ?? 0)}
            </span>
          </div>
          <div className="border-t border-slate-600 my-2"></div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-300">Ukupno</span>
            <span className="font-bold text-green-400">
              {formatCurrency.format((row.amount ?? 0) + (row.taxAmount ?? 0))}
            </span>
          </div>
        </div>
      </Tooltip>
    ),
  },
  {
    title: "",
    key: "expense_actions",
    alwaysVisible: true,
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleExpenseRow) => (
      <ButtonMenu
        endIcon="/media/icons/more-dots.svg"
        cssClassName="h-full"
        variant="text"
      >
        <ButtonMenuItem
          startIcon="/media/icons/trash.svg"
          variant="remove"
          onClick={(e) => onDeleteClick(e, row.id)}
        >
          Obriši
        </ButtonMenuItem>
      </ButtonMenu>
    ),
  },
];

// Helper za caller — vraća rutu za navigaciju na vehicle detail.
export const buildVehicleDetailRoute = (vehicleId: string): string =>
  transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
    id: vehicleId,
  });
